/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@ui/molecules/Modal/Modal';
import { ModalHeader } from '@ui/molecules/Modal/ModalHeader';
import { ModalBody } from '@ui/molecules/Modal/ModalBody';
import { ModalFooter } from '@ui/molecules/Modal/ModalFooter';
import { Button } from '@ui/atoms/Button/Button';
import { Text } from '@ui/atoms/Text/Text';
import { Accordion } from '@ui/molecules/Accordion/Accordion';
import { SideDrawer } from '@ui/molecules/SideDrawer/SideDrawer';
import { SideDrawerSection } from '@ui/molecules/SideDrawer/SideDrawerSection';
import { SideDrawerNavItem } from '@ui/molecules/SideDrawer/SideDrawerNavItem';
import { PROMPT_LIBRARY } from '../../constants/promptLibrary';
import { WIDGET_SCOPE_CLASS } from '../../constants/widgetScope';
import { PromptWithInputs } from '../PromptWithInputs';
import { hasPlaceholders, assemblePrompt } from '../../utils/parsePromptTemplate';
import './PromptLibraryModalStyles.scss';
const BASE_CLASS = 'ai-prompt-library';
function truncateText(text, maxLength) {
    if (text.length <= maxLength)
        return text;
    return `${text.slice(0, maxLength)}…`;
}
function getPromptKey(categoryName, promptTitle) {
    return `${categoryName}::${promptTitle}`;
}
function createInitialPromptStates(library) {
    const states = {};
    library.forEach((cat) => {
        cat.prompts.forEach((prompt) => {
            const key = getPromptKey(cat.category, prompt.title);
            states[key] = { isExpanded: false, inputValues: {}, allFilled: !hasPlaceholders(prompt.prompt) };
        });
    });
    return states;
}
export function PromptLibraryModal({ isOpen, onClose, onUsePrompt, library = PROMPT_LIBRARY }) {
    var _a;
    const [selectedCategory, setSelectedCategory] = useState(((_a = library[0]) === null || _a === void 0 ? void 0 : _a.category) || '');
    const [promptStates, setPromptStates] = useState(() => createInitialPromptStates(library));
    const promptRefs = useRef({});
    const pendingFocusRef = useRef(null);
    useEffect(() => {
        var _a;
        if (!isOpen) {
            setPromptStates(createInitialPromptStates(library));
            setSelectedCategory(((_a = library[0]) === null || _a === void 0 ? void 0 : _a.category) || '');
            return;
        }
        setPromptStates(createInitialPromptStates(library));
        setSelectedCategory((current) => { var _a; return library.some((cat) => cat.category === current) ? current : ((_a = library[0]) === null || _a === void 0 ? void 0 : _a.category) || ''; });
    }, [isOpen, library]);
    const handleCategoryClick = useCallback((category) => {
        setSelectedCategory(category);
        setPromptStates(createInitialPromptStates(library));
    }, [library]);
    const handleToggle = useCallback((promptKey, expanded) => {
        setPromptStates((prev) => (Object.assign(Object.assign({}, prev), { [promptKey]: Object.assign(Object.assign({}, prev[promptKey]), { isExpanded: expanded }) })));
        if (expanded) {
            pendingFocusRef.current = promptKey;
        }
    }, []);
    useEffect(() => {
        var _a;
        const key = pendingFocusRef.current;
        if (key && ((_a = promptStates[key]) === null || _a === void 0 ? void 0 : _a.isExpanded)) {
            const timer = setTimeout(() => {
                var _a;
                (_a = promptRefs.current[key]) === null || _a === void 0 ? void 0 : _a.focusFirstInput();
                pendingFocusRef.current = null;
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [promptStates]);
    const handleInputValuesChange = useCallback((promptKey, values) => {
        setPromptStates((prev) => (Object.assign(Object.assign({}, prev), { [promptKey]: Object.assign(Object.assign({}, prev[promptKey]), { inputValues: values }) })));
    }, []);
    const handleCompletionChange = useCallback((promptKey, allFilled) => {
        setPromptStates((prev) => (Object.assign(Object.assign({}, prev), { [promptKey]: Object.assign(Object.assign({}, prev[promptKey]), { allFilled }) })));
    }, []);
    const handleUsePromptClick = useCallback((promptKey, template) => {
        const state = promptStates[promptKey];
        const promptHasPlaceholders = hasPlaceholders(template);
        if (!promptHasPlaceholders) {
            onUsePrompt(template);
            return;
        }
        if (!(state === null || state === void 0 ? void 0 : state.isExpanded) && (state === null || state === void 0 ? void 0 : state.allFilled)) {
            const assembled = assemblePrompt(template, state.inputValues);
            onUsePrompt(assembled);
            return;
        }
        if (!(state === null || state === void 0 ? void 0 : state.isExpanded)) {
            handleToggle(promptKey, true);
            return;
        }
        if (state.allFilled) {
            const assembled = assemblePrompt(template, state.inputValues);
            onUsePrompt(assembled);
        }
    }, [promptStates, onUsePrompt, handleToggle]);
    const getPreviewText = useCallback((promptKey, template) => {
        const state = promptStates[promptKey];
        if (!state)
            return truncateText(template, 60);
        const hasAnyValue = Object.values(state.inputValues).some((v) => v.trim().length > 0);
        if (hasAnyValue) {
            const assembled = assemblePrompt(template, state.inputValues);
            return truncateText(assembled, 60);
        }
        return truncateText(template, 60);
    }, [promptStates]);
    const isUsePromptDisabled = useCallback((promptKey, template) => {
        const state = promptStates[promptKey];
        if (!state)
            return false;
        if (!hasPlaceholders(template))
            return false;
        if (!state.isExpanded)
            return false;
        return !state.allFilled;
    }, [promptStates]);
    return (React.createElement(Modal, { isOpen: isOpen, onClose: onClose, ariaLabel: "Prompt Library", className: `${BASE_CLASS} ${WIDGET_SCOPE_CLASS}` },
        React.createElement(ModalHeader, { title: "Prompt Library", onClose: onClose }),
        React.createElement(ModalBody, { className: `${BASE_CLASS}__body` },
            React.createElement("div", { className: `${BASE_CLASS}__sidebar` },
                React.createElement(SideDrawer, null,
                    React.createElement(SideDrawerSection, { title: "Prompts by Subject" }, library.map((cat) => (React.createElement(SideDrawerNavItem, { key: cat.category, label: cat.category, onClick: () => handleCategoryClick(cat.category) })))))),
            React.createElement("div", { className: `${BASE_CLASS}__content` }, library
                .filter((cat) => cat.category === selectedCategory)
                .map((cat) => (React.createElement("div", { key: cat.category, className: `${BASE_CLASS}__category` },
                React.createElement(Text, { variant: "small", as: "h3", className: `${BASE_CLASS}__category-title` }, cat.category),
                React.createElement("div", { className: `${BASE_CLASS}__prompts` }, cat.prompts.map((prompt) => {
                    var _a, _b;
                    const promptKey = getPromptKey(cat.category, prompt.title);
                    const state = promptStates[promptKey];
                    const promptHasInputs = hasPlaceholders(prompt.prompt);
                    return (React.createElement(Accordion, { key: prompt.title, title: prompt.title, isExpanded: (_a = state === null || state === void 0 ? void 0 : state.isExpanded) !== null && _a !== void 0 ? _a : false, onToggle: (expanded) => handleToggle(promptKey, expanded), previewContent: React.createElement("span", { className: `${BASE_CLASS}__preview-text` }, getPreviewText(promptKey, prompt.prompt)), actions: [
                            {
                                label: 'Use prompt',
                                buttonProps: {
                                    variant: 'secondary',
                                    disabled: isUsePromptDisabled(promptKey, prompt.prompt),
                                    onClick: () => handleUsePromptClick(promptKey, prompt.prompt)
                                }
                            }
                        ] },
                        React.createElement("div", { className: `${BASE_CLASS}__prompt-full` }, promptHasInputs ? (React.createElement(PromptWithInputs, { ref: (r) => {
                                promptRefs.current[promptKey] = r;
                            }, template: prompt.prompt, inputValues: (_b = state === null || state === void 0 ? void 0 : state.inputValues) !== null && _b !== void 0 ? _b : {}, onInputValuesChange: (values) => handleInputValuesChange(promptKey, values), onCompletionChange: (filled) => handleCompletionChange(promptKey, filled) })) : (React.createElement(Text, { variant: "body" },
                            "\u201C",
                            prompt.prompt,
                            "\u201D")))));
                }))))))),
        React.createElement(ModalFooter, null,
            React.createElement(Button, { variant: "default", onClick: onClose }, "Close"))));
}
